import { useRef, useState } from "react";
import { UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { apiError, formatBytes } from "@/lib/format";

const putChunk = async (uploadId, index, blob, onLoaded, tries = 3) => {
  for (let a = 1; ; a++) {
    try {
      return await adminApi.put(`/admin/apks/uploads/${uploadId}/chunks/${index}`, blob, {
        headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: (p) => onLoaded(p.loaded),
      });
    } catch (e) {
      const status = e?.response?.status;
      if (a >= tries || (status && status < 500)) throw e;
      await new Promise((r) => setTimeout(r, 1000 * a));
    }
  }
};

const uploadError = (e) => {
  const status = e?.response?.status;
  if (status === 413) return "Upload blocked by server size limit. Please try again.";
  if (status === 401) return "Session expired. Please log in again.";
  if (!e?.response) return "Network error during upload. Check your connection and try again.";
  return apiError(e);
};

const Label = ({ htmlFor, children }) => <label htmlFor={htmlFor} className="block text-xs tracking-[0.2em] uppercase text-slate-500 mb-2">{children}</label>;

export const UploadForm = ({ onUploaded }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const today = () => new Date().toLocaleDateString("en-CA");
  const blank = () => ({ version_name: "", version_code: "", release_notes: "", force_update: false, release_date: today() });
  const [f, setF] = useState(blank);
  const [progress, setProgress] = useState(null);

  const pick = async (e) => {
    const fl = e.target.files?.[0];
    if (!fl) return setFile(null);
    if (!fl.name.toLowerCase().endsWith(".apk")) {
      toast.error("Please choose a .apk file");
      e.target.value = "";
      return setFile(null);
    }
    const head = new Uint8Array(await fl.slice(0, 4).arrayBuffer());
    if (!(head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04)) {
      toast.error("Invalid APK: file is not a valid ZIP/APK archive");
      e.target.value = "";
      return setFile(null);
    }
    setFile(fl);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose an APK file first");
    setProgress(0);
    try {
      const { data: init } = await adminApi.post("/admin/apks/uploads", {
        file_name: file.name,
        size: file.size,
        version_name: f.version_name,
        version_code: Number(f.version_code),
        release_notes: f.release_notes,
        force_update: f.force_update,
        release_date: f.release_date,
      });
      for (let i = 0; i < init.total_chunks; i++) {
        const blob = file.slice(i * init.chunk_size, Math.min(file.size, (i + 1) * init.chunk_size));
        await putChunk(init.upload_id, i, blob, (loaded) =>
          setProgress(Math.min(99, Math.round(((i * init.chunk_size + loaded) / file.size) * 100))),
        );
      }
      setProgress(100);
      const { data } = await adminApi.post(`/admin/apks/uploads/${init.upload_id}/complete`, null, { timeout: 600000 });
      toast.success(data.message);
      setF(blank());
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      onUploaded();
    } catch (err) {
      toast.error(uploadError(err));
    } finally {
      setProgress(null);
    }
  };

  const busy = progress !== null;
  return (
    <form onSubmit={submit} className="glass rounded-3xl p-5 sm:p-7 space-y-5" data-testid="upload-form" aria-labelledby="upload-heading">
      <h2 id="upload-heading" className="font-display text-xl font-bold">Upload new release</h2>
      <div>
        <Label htmlFor="u-file">APK file</Label>
        <input ref={fileRef} id="u-file" type="file" accept=".apk,application/vnd.android.package-archive" onChange={pick} className="field file:mr-3 file:rounded-full file:border-0 file:bg-[#00BFFF]/15 file:text-[#7DDCFF] file:px-4 file:py-1.5" data-testid="upload-file-input" />
        {file && <p className="text-xs text-slate-400 mt-2 break-all" data-testid="upload-file-info">{file.name} · {formatBytes(file.size)}</p>}
      </div>
      <div className="grid xs:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="u-name">Version name</Label>
          <input id="u-name" required placeholder="1.2.0" className="field" value={f.version_name} onChange={(e) => setF({ ...f, version_name: e.target.value })} data-testid="upload-version-name-input" />
        </div>
        <div>
          <Label htmlFor="u-code">Version code</Label>
          <input id="u-code" required type="number" min="1" step="1" placeholder="12" className="field" value={f.version_code} onChange={(e) => setF({ ...f, version_code: e.target.value })} data-testid="upload-version-code-input" />
        </div>
      </div>
      <div>
        <Label htmlFor="u-date">Release date</Label>
        <input id="u-date" type="date" required className="field [color-scheme:dark]" value={f.release_date} onChange={(e) => setF({ ...f, release_date: e.target.value })} data-testid="upload-release-date-input" />
      </div>
      <div>
        <Label htmlFor="u-notes">Release notes</Label>
        <textarea id="u-notes" rows={5} className="field" value={f.release_notes} onChange={(e) => setF({ ...f, release_notes: e.target.value })} data-testid="upload-release-notes-input" />
      </div>
      <label className="flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
        <input type="checkbox" className="accent-[#00BFFF] w-5 h-5" checked={f.force_update} onChange={(e) => setF({ ...f, force_update: e.target.checked })} data-testid="upload-force-update-checkbox" />
        Force update (users must install this version)
      </label>
      {busy && (
        <div data-testid="upload-progress">
          <div className="h-2 rounded-full bg-white/5 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full bg-[#00BFFF] transition-[width] duration-200" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-slate-400 mt-2">{progress < 100 ? `Uploading… ${progress}%` : "Processing & publishing…"}</p>
        </div>
      )}
      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="upload-submit-btn">
        <UploadSimple size={18} weight="bold" aria-hidden="true" /> {busy ? "Uploading…" : "Upload & set LIVE"}
      </button>
    </form>
  );
};
